import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Shield, Key, Tag, MapPin, AlertCircle, Workflow } from 'lucide-react';
import { 
  AdminUsersTab, 
  AdminRolesTab, 
  AdminPermissionsTab, 
  AdminStatusesTab,
  AdminStoresTab,
  AdminInvoiceWorkflowTab
} from '@pages/admin';
import { useAuth } from '@contexts/AuthContext';
import toast from 'react-hot-toast';
type AdminTab = 'users' | 'roles' | 'permissions' | 'statuses' | 'stores' | 'workflow';

interface TabConfig {
  id: AdminTab;
  label: string;
  icon: React.ElementType;
  component: React.ComponentType;
}

const tabs: TabConfig[] = [
  {
    id: 'users',
    label: 'Users',
    icon: Users,
    component: AdminUsersTab,
  },
  {
    id: 'roles',
    label: 'Roles',
    icon: Shield,
    component: AdminRolesTab,
  },
  {
    id: 'permissions',
    label: 'Permissions',
    icon: Key,
    component: AdminPermissionsTab,
  },
  {
    id: 'statuses',
    label: 'Statuses',
    icon: Tag,
    component: AdminStatusesTab,
  },
  {
    id: 'stores',
    label: 'Stores',
    icon: MapPin,
    component: AdminStoresTab,
  },
  {
    id: 'workflow',
    label: 'Invoice Workflow',
    icon: Workflow,
    component: AdminInvoiceWorkflowTab,
  },
];

export const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Check if user has admin privileges
  const isAdmin = user?.isAdmin || user?.roles?.some(role => role.name === 'admin') || false;

  useEffect(() => {
    // If not loading and either not authenticated or not admin, redirect
    if (!isLoading) {
      if (!isAuthenticated) {
        toast.error('Please log in to access the admin panel');
        navigate('/auth');
        return;
      }
      
      if (!isAdmin) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/invoices'); // Redirect to a safe page
        return;
      }
    }
  }, [isAuthenticated, isAdmin, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not authenticated or not admin
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-text-primary mb-2">Access Denied</h1>
          <p className="text-text-secondary mb-6">You don't have permission to access the admin panel.</p>
          <button
            onClick={() => navigate('/invoices')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Invoices
          </button>
        </div>
      </div>
    );
  }

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || AdminUsersTab;

  return (
    <div className="min-h-screen bg-background-primary">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Administration
          </h1>
          <p className="text-text-secondary">
            Manage users, roles, permissions, and system settings
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-background-secondary rounded-lg shadow-sm mb-6">
          <div className="border-b border-border-primary">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isSuperAdmin = user?.isAdmin;
                
                // Hide workflow tab for non-superadmin users
                if (tab.id === 'workflow' && !isSuperAdmin) {
                  return null;
                }
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      isActive
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-secondary'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-background-secondary rounded-lg shadow-sm">
          <div className="p-6">
            <ActiveComponent />
          </div>
        </div>
      </div>
    </div>
  );
};
